import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useCountUp } from "../hooks/useAnimations";
import MagneticButton from "./MagneticButton";

const HERO_BG = "/images/neu/Pirate-Bowling-10.jpg";
const HERO_BG2 = "/images/neu/Pirate-Bowling-6.jpg";

/* Inline SVG skull & crossbones – minimal, tasteful */
const SkullSVG = () => (
  <svg className="w-5 h-5 gold-text opacity-60" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A6.99 6.99 0 0 0 19 9a7 7 0 0 0-7-7zM9 18h2v2H9v-2zm4 0h2v2h-2v-2z"/>
  </svg>
);

function AnimatedStat({ value, label }) {
  const isNumeric = /^\d+/.test(value);
  const numMatch = value.match(/^(\d+)(.*)/);
  const { ref, display } = useCountUp(
    isNumeric && numMatch ? parseInt(numMatch[1], 10) : 0,
    1.8
  );

  return (
    <div className="text-center" ref={ref}>
      <div className="font-unbounded text-2xl font-black gold-text">
        {isNumeric && numMatch ? `${display}${numMatch[2]}` : value}
      </div>
      <div className="font-space-grotesk text-xs text-cream/60 uppercase tracking-widest mt-1">
        {label}
      </div>
    </div>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const orbY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_BG}
          alt="Pirate Bowling Center Velburg Atmosphäre"
          loading="eager"
          className="w-full h-full object-cover kenburns"
          onError={(e) => { e.target.src = HERO_BG2; }}
        />
        {/* Dark dramatic overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy/55 via-navy/35 to-navy/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/40 via-transparent to-navy/40" />
        {/* Warm gold tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-gold/5 via-transparent to-transparent" />
      </motion.div>

      {/* Ambient glow orbs */}
      <motion.div className="hidden 2xl:block absolute top-1/3 left-1/4 w-80 h-80 rounded-full bg-gold/12 blur-[120px] pointer-events-none float" style={{ y: orbY }} />
      <motion.div className="hidden 2xl:block absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full bg-gold/8 blur-[100px] pointer-events-none float" style={{ y: orbY, animationDelay: "2s" }} />

      {/* Content */}
      <div className="relative z-10 text-center px-6 md:px-12 max-w-5xl mx-auto pt-20 md:pt-24 pb-8 md:pb-16">

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="skull-divider mb-6"
          data-testid="hero-badge"
        >
          <span className="font-space-grotesk text-xs text-gold tracking-[0.35em] uppercase px-4">
            Velburg · Oberpfalz
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="font-unbounded leading-none tracking-tight mb-6"
          data-testid="hero-headline"
        >
          <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-cream">
            PIRATE
          </span>
          <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black gold-gradient-text">
            BOWLING
          </span>
          <span className="block text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-cream/70 tracking-[0.3em] mt-1">
            CENTER
          </span>
        </motion.h1>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mx-auto w-24 h-px bg-gradient-to-r from-transparent via-gold to-transparent mb-5"
        />

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="font-space-grotesk text-base md:text-lg text-cream/90 max-w-xl mx-auto mb-8 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Bowling, Drinks und Entertainment – erlebe einen Abend voller Spaß und Abenteuer.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          data-testid="hero-cta-buttons"
        >
          <MagneticButton>
            <a
              href="https://4bowl.de/pirate-bowling/online/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-9 py-4 rounded-full text-sm inline-block"
              data-testid="hero-cta-reserve"
            >
              Bahn reservieren
            </a>
          </MagneticButton>
          <MagneticButton>
            <button
              onClick={() => document.querySelector("#events")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-outline px-9 py-4 rounded-full text-sm"
              data-testid="hero-cta-event"
            >
              Event planen
            </button>
          </MagneticButton>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="flex items-center justify-center gap-4 sm:gap-6 md:gap-10 mt-12 flex-wrap"
          data-testid="hero-stats"
        >
          {[
            { value: "6", label: "Attraktionen" },
            { value: "Mi–So", label: "Geöffnet" },
            { value: "Kult", label: "Oberpfalz" },
          ].map((s, i) => (
            <AnimatedStat key={i} value={s.value} label={s.label} />
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        onClick={() => document.querySelector("#entertainment")?.scrollIntoView({ behavior: "smooth" })}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/50 hover:text-gold transition-colors float"
        aria-label="Scrollen"
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown className="w-7 h-7" />
      </motion.button>
    </section>
  );
}
